/**
 * Configuration Template Types
 *
 * Configuration templates store reusable device configurations with:
 * - client_id: Client this template belongs to
 * - device_type: Device type code (fetched dynamically from /v2/device-types)
 * - priority: Template priority (1-100, higher = more priority)
 * - name: Unique template name
 * - json_config: JSON object with all device-type specific configuration
 */

/**
 * Hardware type code for templates - fetched from /v2/hardware-types API.
 * Examples: 'PGA', 'IOTL', 'P4GU', 'TMS2', 'UNKN'.
 */
export type DeviceType = string;

/**
 * Custom field for device-specific configuration.
 * Name and value are added to effective_config.
 * Group and notes are metadata for organization.
 */
export interface CustomField {
  name: string;
  value: string | number | boolean;
  group?: string;
  notes?: string;
  source?: 'device_type' | 'hardware_field_def' | 'template' | 'device';
  default?: string | number | boolean;
  type?: 'string' | 'number' | 'boolean';
}

/**
 * Custom field definition from a device type's default_settings.
 * Defines the schema (name, type, default) for custom fields.
 */
export interface CustomFieldDefinition {
  name: string;
  type: 'string' | 'number' | 'boolean';
  default: string | number | boolean;
  group?: string;
  notes?: string;
}

/**
 * JSON configuration structure for device templates.
 * Contains all device-type specific configuration fields.
 */
export interface JsonConfig {
  // Syslog configuration
  syslog_host?: string;
  syslog_port?: number;
  syslog_facility?: number;

  // MQTT configuration
  mqtt_host?: string;
  mqtt_port?: number;
  mqtt_user?: string;
  mqtt_pass?: string;

  // Provisioning configuration
  provisioning?: string;
  keepalive_timer?: number;

  // Device password configuration
  password?: string;
  generate_password?: boolean;

  // Device-specific custom fields
  custom_fields?: CustomField[];

  // Allow additional device-type specific fields
  [key: string]: string | number | boolean | CustomField[] | undefined;
}

/**
 * Full configuration template with all fields.
 * Used in detail/edit views where complete data is needed.
 */
export interface ConfigTemplate {
  id: string;
  name: string;
  description?: string;
  client_id: string;
  client_ids?: string[];  // Multi-client support
  device_type: DeviceType;
  priority: number;
  json_config: JsonConfig;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Configuration template list item (excludes json_config for performance).
 * Used in table/list views.
 */
export interface ConfigTemplateListItem {
  id: string;
  name: string;
  description?: string;
  client_id: string;
  client_ids?: string[];  // Multi-client support
  device_type: DeviceType;
  priority: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Request model for creating a new configuration template.
 */
export interface ConfigTemplateCreate {
  name: string;
  description?: string;
  client_id?: string;
  client_ids?: string[];  // Multi-client support
  device_type: DeviceType;
  priority: number;
  json_config: JsonConfig;
}

/**
 * Request model for updating an existing configuration template.
 */
export interface ConfigTemplateUpdate {
  name?: string;
  description?: string;
  client_id?: string;
  client_ids?: string[];
  device_type?: DeviceType;
  priority?: number;
  json_config?: JsonConfig;
  is_active?: boolean;
}

/**
 * Configuration template statistics.
 */
export interface ConfigTemplateStats {
  total_templates: number;
  active_templates: number;
  templates_by_type: Record<string, number>;
  templates_by_priority: Record<string, number>;
}

/**
 * Default JSON configuration values for new templates.
 */
export const DEFAULT_JSON_CONFIG: JsonConfig = {
  syslog_host: '',
  syslog_port: 514,
  syslog_facility: 0,
  mqtt_host: '',
  mqtt_port: 1883,
  mqtt_user: '',
  mqtt_pass: '',
  provisioning: '',
  keepalive_timer: 60
};

/**
 * Priority level labels for UI display.
 */
export const PRIORITY_LEVELS = {
  HIGH: { min: 76, max: 100, label: 'High', color: '#dc3545' },
  MEDIUM: { min: 51, max: 75, label: 'Medium', color: '#fd7e14' },
  NORMAL: { min: 26, max: 50, label: 'Normal', color: '#28a745' },
  LOW: { min: 1, max: 25, label: 'Low', color: '#6c757d' }
} as const;

/**
 * Get priority level label based on priority value.
 */
export function getPriorityLevel(priority: number): typeof PRIORITY_LEVELS[keyof typeof PRIORITY_LEVELS] {
  if (priority >= 76) return PRIORITY_LEVELS.HIGH;
  if (priority >= 51) return PRIORITY_LEVELS.MEDIUM;
  if (priority >= 26) return PRIORITY_LEVELS.NORMAL;
  return PRIORITY_LEVELS.LOW;
}
